/**
 * useTutorLiveChrome — la barre, « Plus », le chat, l'aide et la fin du live du masteur.
 *
 * Le port de l'ancien pied du tuteur (`common/header/LiveModeFooter.js`, sa `ControlBarM`, son ⋮, son
 * `TroubleshootingPanel`, sa `LiveHelpModal` et sa boîte de fin), sur la barre du design system
 * (`<TutorLiveControlBar>`), les feuilles de `<TutorLiveOverlays>` et le chat en volet
 * (`<LiveChat viewer="tutor">`). Chaque appel d'API et chaque message de salle passe par
 * `tutorLiveChromeProtocol` (`TUTOR_LIVE_CHROME_EQUIVALENCE`, une ligne par geste) ; les règles pures
 * sont dans `tutorLiveChromeViewModel`.
 *
 * Ce qu'il rend :
 *  - `bar` : le micro et la caméra (capture `echoCancellation` + `noiseSuppression` ; un défaut porte le
 *    point ; en natif, toucher un appareil en défaut ouvre l'aide), leurs chevrons (les appareils, le
 *    web), le partage (`presentation.screenShare`), la mosaïque (« Interrompre la présentation ? » si
 *    « Présenter » tourne), le chat et son compte de non-lus (**corrigé** : la somme des fils privés et
 *    du groupe — l'ancien point ne s'affichait jamais dès qu'un fil privé existait), « Plus » et son
 *    point (un problème détecté), la fin ;
 *  - `overlays` : « Plus » — dont ce que portait l'ancien bandeau (round 4 : il est retiré) : « Tous en
 *    Concentré » (en tête, la page le tait en individuel), « Prendre RDV avec un coordinateur » (séance
 *    d'essai, au moins un élève inscrit, en dernière ligne) ; « Générer une activité » pour le groupe est le
 *    champ du fil sans filtre (« Tous »), ou « Nouvelle activité commune » —, « Resynchroniser » (la
 *    relecture du fil : `options.refresh`), la fin,
 *    « Un problème ? » (ses gestes : « J'ai autorisé… », « Changer de micro / de caméra », qui ferme l'aide
 *    et ouvre la fenêtre des appareils), le chat ;
 *  - `panes` : les colonnes repliées — d'office au début d'une présentation sur une tablette, comme l'ancien
 *    focus ;
 *  - `layer` : une boîte (une erreur, « Interrompre la présentation ? », « Crée plutôt une activité »),
 *    une snackbar (sorti de son écran d'activité depuis 30 s, la caméra masquée, le signalement envoyé),
 *    une feuille (les appareils, les ressources du tuteur, l'aperçu d'une pièce, la visionneuse, le vocal).
 *    Les deux premières snackbars s'ouvrent seules (`snackbarAutomatic`) : pendant une présentation, recadrée
 *    ou non, elles attendent qu'elle finisse ; les boîtes et l'accusé du signalement suivent un geste.
 *
 * La génération d'une activité (« Nouvelle activité commune », « Créer une activité » depuis une pièce
 * du chat) vit dans le fil (décision 10) : l'écran la branche par `options.generate`. Sans elle, l'élève
 * visé devient l'élève regardé (`selection.select`), et le champ du fil génère pour lui, ou pour toute
 * le groupe sans filtre (G-17).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useWindowDimensions } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle } from "../../../../../../data/redux/actions/api"
import { liveService } from "../../../../../../data/services/liveService"
import { openCalendly } from "../../../../../../data/utility/Utils"
import {
    type TutorLiveBarZone,
    type TutorLiveOverlaysZone,
    type TutorLivePanesZone,
} from "../../../../../../design-system/pages/TutorLivePage/TutorLivePage"
import { type UILiveDeviceTab } from "../../../../../../design-system/templates/LiveDevicesSheet"
import { type UITutorLiveSnackbar } from "../../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"
import { useCameraStateRecorder } from "../../../../../../engine/live/room/cameraTelemetry"
import { useCameraFlip } from "../../../../../../engine/live/room/useCameraFlip"
import { useDisconnectFromLive } from "../../../../../../engine/live/room/useDisconnectFromLive"
import { useMediaPermissions } from "../../../../../../engine/live/room/useMediaPermissions"
import { useNoiseFilter } from "../../../../../../engine/live/room/useNoiseFilter"
import { useRoomDevices } from "../../../../../../engine/live/room/useRoomDevices"
import { useMicrophoneLevel } from "../../../../../../engine/precall/useMicrophoneLevel"
import { apiCall, apiFailureOf, type ApiCallParams, type ApiService } from "../../../../../../engine/shared/apiCall"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { API_URL } from "../../../../../../data/constants/apiConstants"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { track } from "../../../hooks/trackEvent"
import { type UILiveProblem } from "../../../types/studentLive"
import { type UITutor } from "../../../types/tutors"
import { type UITutorLiveStudent } from "../../../types/tutorLive"
import {
    TUTOR_GROUP_CHANNEL,
    tutorLiveStudentsOf,
    type ApiTutorLiveSession,
} from "../../../../adapters/tutor/Live/tutorLiveChatAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import {
    CAMERA_OBSTRUCTED_MESSAGE,
    OUT_OF_APP_ALERT_SECONDS,
    outOfAppMessage,
    outOfAppWatchedIds,
    tutorCanToggleDevices,
    tutorChatUnreadCount,
    tutorDetectedProblem,
    tutorDeviceTap,
    tutorLiveFinalized,
    tutorMoreActions,
    type TutorHelpContext,
    type TutorMoreActionId,
} from "../../../../view-models/tutor/Live/tutorLiveChromeViewModel"
import { useTutorResourcesState } from "../Account/useTutorResourcesState"
import {
    type TutorLiveLayer,
    type TutorLivePresentation,
    type TutorLiveRoomContext,
    type TutorLiveSelection,
} from "./tutorLiveContract"
import { useTutorLiveChromeChat, type TutorLiveGenerateRequest } from "./useTutorLiveChromeChat"
import { useTutorLiveChromeHelp } from "./useTutorLiveChromeHelp"
import { useTutorLiveChromeObstruction } from "./useTutorLiveChromeObstruction"
import { TUTOR_LIVE_EVENTS, tutorLiveChromeProtocol } from "./useTutorLiveChromeProtocol"
import { renderTutorLiveChromeSheets } from "./useTutorLiveChromeSheets"

export { TUTOR_LIVE_CHROME_EQUIVALENCE } from "./useTutorLiveChromeProtocol"
export type { TutorLiveGenerateRequest } from "./useTutorLiveChromeChat"

/** La page retire le partage de la barre au téléphone (`TutorLivePage`, sous 600 px) : il passe dans « Plus ». */
const SHARE_IN_MORE_BELOW = 600
/** L'ancien focus repliait les colonnes au début d'une présentation sur une tablette. */
const COLLAPSE_ON_PRESENT_BELOW = 1280
/** « Interrompre » : la grille arrive 300 ms après l'arrêt de la présentation (`TutorLiveScreen.js:92-108`). */
const MOSAIC_AFTER_PRESENT_MS = 300
/** Le micro ouvert sans un bruit pendant ce délai, panneau ouvert : muet au niveau du système. */
const MIC_SILENT_MS = 3000
/** L'accusé du signalement (`LiveHelpModal.js`). */
const REPORT_SENT = "Signalement envoyé, merci !"
const ERROR_TITLE = "Erreur"

export type TutorLiveChromeOptions = {
    /** La génération du fil : pour ces élèves (vide : tout le groupe), sur ce fichier. */
    generate?: (request: TutorLiveGenerateRequest) => void
    /**
     * « Resynchroniser » dans « Plus » : la relecture du fil (`useTutorLiveFeed.refresh`, l'« Actualiser »
     * de `NewStudentsListItem`). Absent : pas de ligne.
     */
    refresh?: { onRefresh: () => void; refreshing: boolean }
}

export type TutorLiveChrome = {
    bar: TutorLiveBarZone
    overlays: Pick<TutorLiveOverlaysZone, "more" | "end" | "help" | "chat">
    panes?: TutorLivePanesZone
    layer: TutorLiveLayer
}

type LiveModeState = {
    liveMode?: {
        todayLiveSessionLogs?: ({ action?: string | null } | null)[] | null
        isLocalPoorConnection?: boolean
        outOfAppIds?: (string | number)[] | null
        activityCameraOnIds?: (string | number)[] | null
    }
}

type AccountState = {
    id?: number | string | null
    live_sessions?: (ApiTutorLiveSession | null)[] | null
}

type LocalParticipantLike = {
    setMicrophoneEnabled?: (enabled: boolean, options?: Record<string, unknown>) => Promise<unknown>
    setCameraEnabled?: (enabled: boolean) => Promise<unknown>
    permissions?: { canPublish?: boolean } | null
    connectionQuality?: string
    getTrackPublication?: (source: never) => { track?: { mediaStreamTrack?: unknown } } | undefined
}

type ErrorDialog = { message: string }

/** La snackbar de la barre ; `automatic` : ouverte sans geste du masteur (`TutorLiveLayer.snackbarAutomatic`). */
type ChromeSnackbar = { value: UITutorLiveSnackbar; automatic: boolean }

const NO_LOGS: ({ action?: string | null } | null)[] = []

export function useTutorLiveChrome(
    room: TutorLiveRoomContext,
    selection: TutorLiveSelection,
    presentation: TutorLivePresentation,
    students: UITutorLiveStudent[],
    options: TutorLiveChromeOptions = {}
): TutorLiveChrome {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as { push: (route: string, params?: object) => void }
    const { width } = useWindowDimensions()
    const { telemetry, liveSessionId } = room
    const account = useSelector((state: unknown) => selectAccount(state as never) as AccountState | null)
    const logs = useSelector((state: LiveModeState) => state.liveMode?.todayLiveSessionLogs) ?? NO_LOGS
    const poorConnection = useSelector((state: LiveModeState) => state.liveMode?.isLocalPoorConnection === true)
    const outOfAppIds = useSelector((state: LiveModeState) => state.liveMode?.outOfAppIds)
    const activityCameraOnIds = useSelector((state: LiveModeState) => state.liveMode?.activityCameraOnIds)
    const session = useMemo(
        () => account?.live_sessions?.find((item) => item != null && String(item.id) === String(liveSessionId)) ?? null,
        [account?.live_sessions, liveSessionId]
    )
    const roster = useMemo(() => tutorLiveStudentsOf(session), [session])

    /* ── la salle et mes appareils ── */
    const local = room.live?.participants.find((participant) => participant.isLocal) ?? null
    const micOn = local?.micOn === true
    const cameraOn = local?.cameraOn === true
    const localParticipant = (room.live?.room?.localParticipant ?? null) as LocalParticipantLike | null
    const canPublish = room.connected && localParticipant?.permissions?.canPublish !== false
    const permissions = useMediaPermissions()
    const devices = useRoomDevices(telemetry)
    const noise = useNoiseFilter(telemetry)
    const flip = useCameraFlip(devices?.camera ?? null, telemetry)
    const obstruction = useTutorLiveChromeObstruction({ enabled: IS_WEB, cameraOn, localParticipant })
    const recordCamera = useCameraStateRecorder() as unknown as (enabled: boolean) => void
    const { disconnectFromLive } = useDisconnectFromLive() as { disconnectFromLive: () => Promise<void> }

    // Le relevé de la caméra : chaque état tenu 2 s s'écrit (`cam_toggle`, la durée caméra du tuteur).
    useEffect(() => {
        if (local != null) recordCamera(cameraOn)
    }, [cameraOn, local != null, recordCamera]) // eslint-disable-line react-hooks/exhaustive-deps

    /* ── l'état de l'interface ── */
    const [moreOpen, setMoreOpen] = useState(false)
    const [endOpen, setEndOpen] = useState(false)
    const [ending, setEnding] = useState<"finalizing" | "leaving" | null>(null)
    const [helpRequest, setHelpRequest] = useState<{ problem: UILiveProblem | null } | null>(null)
    const [devicesTab, setDevicesTab] = useState<UILiveDeviceTab | null>(null)
    const [resourcesOpen, setResourcesOpen] = useState(false)
    const [mosaic, setMosaic] = useState(false)
    const [presentGuard, setPresentGuard] = useState(false)
    const [errorDialog, setErrorDialog] = useState<ErrorDialog | null>(null)
    const [snackbar, setSnackbar] = useState<ChromeSnackbar | null>(null)
    const [panes, setPanes] = useState({ filmstripCollapsed: false, feedCollapsed: false })
    const resources = useTutorResourcesState()

    /* ── le niveau du micro, panneau ouvert seulement (`useTroubleshootingDetection.js:35-49`) ── */
    const listening = helpRequest != null && permissions.microphone !== false
    const micLevel = useMicrophoneLevel(
        listening ? { microphone: true } : null,
        devices?.microphone.activeId ?? undefined
    ) as number
    const [micSilent, setMicSilent] = useState(false)
    useEffect(() => {
        const shouldWork = listening && micOn
        if (!shouldWork || micLevel > 0) {
            setMicSilent(false)
            return undefined
        }
        const timer = setTimeout(() => setMicSilent(true), MIC_SILENT_MS)
        return () => clearTimeout(timer)
    }, [listening, micOn, micLevel])

    const detected = tutorDetectedProblem({
        poorConnection,
        micAllowed: permissions.microphone,
        micSilent,
        cameraAllowed: permissions.camera,
    })
    const micBlocked = permissions.microphone === false || micSilent
    const cameraBlocked = permissions.camera === false || obstruction.obstructed

    /* ── le protocole ── */
    const obstructionRef = useRef(obstruction)
    obstructionRef.current = obstruction
    const roomRef = useRef(room)
    roomRef.current = room
    const protocol = useMemo(
        () =>
            tutorLiveChromeProtocol({
                call: <T>(service: ApiService, params?: ApiCallParams) =>
                    apiCall<T>(dispatch as never, service, params),
                send: (type, data, to, policy) =>
                    roomRef.current.live?.send(type, data, to, policy) ??
                    Promise.resolve({ ok: false as const, reason: "dropped" as const }),
                track,
                navigate: (route, params) => navigation.push(route, params),
                closeOutObstruction: () => obstructionRef.current.closeOut(),
                closeOutage: () => roomRef.current.live?.closeOutage(),
                connection: () => roomRef.current.live?.state.connection ?? null,
                disconnect: () => {
                    disconnectFromLive().catch((cause: unknown) => telemetry.error("disconnect_from_live", cause))
                    dispatch(apiIdle(liveService.sendSatisfaction) as never)
                    dispatch(apiIdle(liveService.sendStudentComment) as never)
                },
            }),
        // La salle et l'obstruction se lisent au geste (`roomRef`, `obstructionRef`).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, navigation, disconnectFromLive, telemetry]
    )
    const showError = useCallback(
        (cause: unknown, fallback: string) =>
            setErrorDialog({ message: apiErrorMessage(apiFailureOf(cause), fallback) }),
        []
    )

    /* ── la génération (le fil) ── */
    const generate = useCallback(
        (request: TutorLiveGenerateRequest) => {
            if (options.generate != null) {
                options.generate(request)
                return
            }
            selection.select(request.studentIds.length === 1 ? request.studentIds[0] : undefined)
        },
        // `options.generate` est lu au geste.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selection.select]
    )

    /* ── les gestes de groupe ── */
    const studentIds = useMemo(() => roster.map((member) => member.id as number | string), [roster])
    const setAllSoundMode = useCallback(
        (enabled: boolean) => {
            if (liveSessionId == null) return
            protocol
                .setAllSoundMode(liveSessionId, enabled, studentIds)
                .catch((cause: unknown) => showError(cause, "Le mode des élèves n'a pas pu changer."))
        },
        [liveSessionId, protocol, studentIds, showError]
    )

    const openHelp = useCallback((problem: UILiveProblem | null) => {
        setMoreOpen(false)
        setDevicesTab(null)
        setHelpRequest({ problem })
    }, [])

    const onMoreAction = (actionId: string) => {
        setMoreOpen(false)
        switch (actionId as TutorMoreActionId) {
            case "all_dialogue":
                setAllSoundMode(true)
                return
            case "lower_hands":
                if (liveSessionId != null)
                    protocol
                        .lowerAllHands(liveSessionId)
                        .catch((cause: unknown) => showError(cause, "Les mains n'ont pas pu être baissées."))
                return
            case "common_activity":
                generate({ studentIds: [] })
                return
            case "refresh":
                // Pendant que ses lectures volent (l'ancien bouton, `disabled={refreshing}`), c'est
                // `onRefresh` qui se garde (`useTutorLiveFeed`) : la barre ne le redit pas.
                options.refresh?.onRefresh()
                return
            case "resources":
                setResourcesOpen(true)
                return
            case "help":
                openHelp(detected)
                return
            case "share_screen":
                presentation.screenShare?.onToggle()
                return
            case "flip_camera":
                flip?.flip()
                return
        }
    }

    /* ── le micro et la caméra ── */
    const toggleDevice = (device: "mic" | "camera") => {
        const tap = tutorDeviceTap({ device, blocked: device === "mic" ? micBlocked : cameraBlocked, native: !IS_WEB })
        if (tap.kind === "help") {
            openHelp(tap.problem)
            return
        }
        if (!tutorCanToggleDevices(canPublish) || localParticipant == null) return
        if (device === "mic") {
            localParticipant
                .setMicrophoneEnabled?.(!micOn, { echoCancellation: true, noiseSuppression: true })
                ?.catch((cause: unknown) => telemetry.error("toggle_mic", cause))
        } else {
            localParticipant
                .setCameraEnabled?.(!cameraOn)
                ?.catch((cause: unknown) => telemetry.error("toggle_camera", cause))
        }
    }

    /* ── la mosaïque, et la garde de la présentation ── */
    const mosaicTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => () => clearTimeout(mosaicTimer.current ?? undefined), [])
    const toggleMosaic = () => {
        if (!mosaic && presentation.on) {
            setPresentGuard(true)
            return
        }
        setMosaic(!mosaic)
    }

    /*
     * ── un élève regardé referme la mosaïque ──
     * L'ancienne grille épinglait l'élève touché et passait en focus (`TutorRoomView.js:104-110`,
     * `TutorGridLayoutM.js:59`), comme une carte du panneau d'événements (`TutorLiveScreen.js:269-273`) : un
     * autre élève regardé, d'où qu'il vienne, ramène la salle autour de lui.
     */
    const selectedRef = useRef(selection.selectedId)
    useEffect(() => {
        if (selectedRef.current === selection.selectedId) return
        selectedRef.current = selection.selectedId
        if (selection.selectedId != null) setMosaic(false)
    }, [selection.selectedId])

    /* ── les colonnes repliées pendant une présentation sur tablette ── */
    const panesBefore = useRef<typeof panes | null>(null)
    useEffect(() => {
        if (presentation.on && width < COLLAPSE_ON_PRESENT_BELOW) {
            setPanes((current) => {
                panesBefore.current = current
                return { filmstripCollapsed: true, feedCollapsed: true }
            })
        } else if (!presentation.on && panesBefore.current != null) {
            const before = panesBefore.current
            panesBefore.current = null
            setPanes(before)
        }
        // La largeur au début de la présentation seulement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presentation.on])

    /* ── sorti de son écran d'activité depuis 30 s (`OutOfAppAlert`) ── */
    const watched = outOfAppWatchedIds({ studentIds: studentIds.map(String), outOfAppIds, activityCameraOnIds })
    const watchedKey = watched.join(",")
    const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
    const rosterRef = useRef(roster)
    rosterRef.current = roster
    const studentsRef = useRef(students)
    studentsRef.current = students
    useEffect(() => {
        const current = new Set(watchedKey === "" ? [] : watchedKey.split(","))
        for (const [id, timer] of timers.current) {
            if (current.has(id)) continue
            clearTimeout(timer)
            timers.current.delete(id)
        }
        for (const id of current) {
            if (timers.current.has(id)) continue
            timers.current.set(
                id,
                setTimeout(() => {
                    timers.current.delete(id)
                    const member = rosterRef.current.find((item) => String(item.id) === id)
                    const shown = studentsRef.current.find((student) => student.id === id)
                    setSnackbar({
                        value: { message: outOfAppMessage(member?.display_name ?? shown?.firstname), tone: "info" },
                        automatic: true,
                    })
                }, OUT_OF_APP_ALERT_SECONDS * 1000)
            )
        }
    }, [watchedKey])
    useEffect(() => {
        const all = timers.current
        return () => {
            for (const timer of all.values()) clearTimeout(timer)
            all.clear()
        }
    }, [])

    // La caméra masquée : l'alerte ouverte d'office, que la croix referme jusqu'à la prochaine.
    useEffect(() => {
        if (obstruction.alertVisible)
            setSnackbar({ value: { message: CAMERA_OBSTRUCTED_MESSAGE, tone: "info" }, automatic: true })
    }, [obstruction.alertVisible])

    /* ── la fin ── */
    const finalized = tutorLiveFinalized(logs)
    const finalize = () => {
        if (liveSessionId == null || ending != null) return
        track(TUTOR_LIVE_EVENTS.finalize)
        setEnding("finalizing")
        protocol
            .finalize(liveSessionId, account?.id, () => setEndOpen(false))
            .catch((cause: unknown) => showError(cause, "Le live n'a pas pu être clôturé."))
            .finally(() => setEnding(null))
    }
    const leave = () => {
        if (liveSessionId == null || ending != null) return
        track(finalized ? TUTOR_LIVE_EVENTS.leaveFinalized : TUTOR_LIVE_EVENTS.leaveOpen)
        setEnding("leaving")
        protocol
            .leave(liveSessionId)
            .then(() => setEndOpen(false))
            .catch((cause: unknown) => showError(cause, "Tu n'as pas pu quitter le live."))
            .finally(() => setEnding(null))
    }

    /* ── le chat ── */
    const chat = useTutorLiveChromeChat({
        session,
        open: selection.chat.open,
        channel: selection.chat.channel,
        onChangeChannel: selection.openChat,
        onClose: selection.closeChat,
        protocol,
        telemetry,
        apiUrl: API_URL as string,
        onOpenActivity: selection.openActivityFor,
        onGenerate: generate,
    })

    /* ── l'aide ── */
    const quality = localParticipant?.connectionQuality
    const helpContext: TutorHelpContext = {
        web: IS_WEB,
        micOn,
        cameraOn,
        micAllowed: permissions.microphone,
        cameraAllowed: permissions.camera,
        micSilent,
        poorConnection,
        quality:
            quality === "excellent" || quality === "good" || quality === "poor" || quality === "lost"
                ? quality
                : "unknown",
    }
    const help = useTutorLiveChromeHelp({
        request: helpRequest,
        onClose: () => setHelpRequest(null),
        context: helpContext,
        liveSessionId,
        logs,
        protocol,
        track,
        telemetry,
        onReported: () => setSnackbar({ value: { message: REPORT_SENT, tone: "success" }, automatic: false }),
        // « Changer de micro / de caméra » : l'aide se ferme sur la fenêtre des appareils, dont « Un problème ? »
        // la rouvre (l'ancien posait le sélecteur par-dessus le panneau ; deux feuilles ne s'empilent pas ici).
        onOpenDevices:
            devices != null
                ? (tab) => {
                      setHelpRequest(null)
                      setDevicesTab(tab)
                  }
                : undefined,
    })

    /* ── la séance d'essai ── */
    const firstStudent = roster[0]
    const onBookCall =
        room.roomKind === "trial" && firstStudent != null ? () => openCalendly(firstStudent, "trial-done") : undefined

    /* ── ce qui se pose par-dessus ── */
    const dialog: TutorLiveLayer["dialog"] =
        errorDialog != null
            ? {
                  value: {
                      kind: "custom",
                      title: ERROR_TITLE,
                      message: errorDialog.message,
                      confirmLabel: "OK",
                  },
                  onConfirm: () => setErrorDialog(null),
                  onDismiss: () => setErrorDialog(null),
              }
            : presentGuard
              ? {
                    value: { kind: "interrupt_presentation" },
                    onConfirm: () => {
                        setPresentGuard(false)
                        presentation.stop()
                        mosaicTimer.current = setTimeout(() => setMosaic(true), MOSAIC_AFTER_PRESENT_MS)
                    },
                    onCancel: () => setPresentGuard(false),
                    onDismiss: () => setPresentGuard(false),
                }
              : chat.dialog

    const sheet = renderTutorLiveChromeSheets({
        devices:
            devices != null && devicesTab != null
                ? {
                      devices,
                      tab: devicesTab,
                      onChangeTab: setDevicesTab,
                      onClose: () => setDevicesTab(null),
                      micMeasurable: permissions.microphone !== false,
                      cameraPreview: room.renderVideo?.(room.me.id),
                      onOpenHelp: () => openHelp(detected),
                      noise,
                  }
                : null,
        resources: resourcesOpen ? { ...resources, onClose: () => setResourcesOpen(false) } : null,
        preview: chat.preview,
        viewer: chat.viewer,
        closeViewer: chat.closeViewer,
        recorder: chat.recorder,
    })

    return {
        bar: {
            micOn,
            onToggleMic: () => toggleDevice("mic"),
            cameraOn,
            onToggleCamera: () => toggleDevice("camera"),
            micBlocked,
            cameraBlocked,
            onOpenMicDevices: devices != null ? () => setDevicesTab("microphone") : undefined,
            onOpenCameraDevices: devices != null ? () => setDevicesTab("camera") : undefined,
            screenShare:
                presentation.screenShare != null && canPublish
                    ? {
                          on: presentation.screenShare.on,
                          pending: presentation.screenShare.pending,
                          onToggle: presentation.screenShare.onToggle,
                      }
                    : undefined,
            mosaic: { on: mosaic, onToggle: toggleMosaic },
            unreadCount: tutorChatUnreadCount(session),
            // Le bouton chat ouvre « Tous » (`liveChatUserId: 0`).
            onOpenChat: () => selection.openChat(TUTOR_GROUP_CHANNEL),
            problemDetected: detected != null,
            onOpenMore: () => setMoreOpen(true),
            onEnd: () => {
                protocol.exitPressed()
                setEndOpen(true)
            },
        },
        overlays: {
            more: {
                onAllFocused: () => {
                    setMoreOpen(false)
                    setAllSoundMode(false)
                },
                onBookCall,
                open: moreOpen,
                onClose: () => setMoreOpen(false),
                actions: tutorMoreActions({
                    roomKind: room.roomKind,
                    shareInMore: width < SHARE_IN_MORE_BELOW,
                    canShare: presentation.screenShare != null && canPublish,
                    sharing: presentation.screenShare?.on === true,
                    canFlip: flip != null,
                    refresh: options.refresh != null ? { refreshing: options.refresh.refreshing } : null,
                }),
                onAction: onMoreAction,
            },
            end: {
                visible: endOpen,
                finalized,
                onFinalize: finalize,
                onLeave: leave,
                onCancel: () => {
                    track(finalized ? TUTOR_LIVE_EVENTS.finalizedClosed : TUTOR_LIVE_EVENTS.openClosed)
                    setEndOpen(false)
                },
                finalizing: ending === "finalizing",
                leaving: ending === "leaving",
            },
            help,
            chat: chat.props != null ? { ...chat.props, tutor: tutorOf(room) } : null,
        },
        panes: {
            ...panes,
            onToggleFilmstrip: () =>
                setPanes((current) => ({ ...current, filmstripCollapsed: !current.filmstripCollapsed })),
            onToggleFeed: () => setPanes((current) => ({ ...current, feedCollapsed: !current.feedCollapsed })),
        },
        layer: {
            dialog,
            // Une erreur passe devant les boîtes de la salle d'aujourd'hui ; les autres derrière elles.
            dialogPriority: errorDialog != null ? 50 : 10,
            snackbar:
                snackbar != null
                    ? {
                          value: snackbar.value,
                          onDismiss: () => {
                              if (snackbar.value.message === CAMERA_OBSTRUCTED_MESSAGE) obstruction.dismiss()
                              setSnackbar(null)
                          },
                      }
                    : null,
            ...(snackbar?.automatic === true ? { snackbarAutomatic: true } : {}),
            sheet,
        },
    }
}

/** Le masteur, tel que `<LiveChat>` le prend (`UITutor`) : il n'est jamais son propre interlocuteur. */
function tutorOf(room: TutorLiveRoomContext): UITutor {
    const [firstname, ...rest] = room.me.name.split(" ")
    return {
        id: room.me.id,
        firstname: firstname ?? "",
        lastname: rest.join(" "),
        ...(room.me.avatarUrl != null ? { avatarUrl: room.me.avatarUrl } : {}),
    }
}
